import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Dialog, 
    Typography, 
    Button, 
    IconButton, 
    Stack,
    Avatar,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import VoiceRecorder from '../../../components/shared/VoiceRecorder';
import { format } from 'date-fns';

import { USERS } from '../ChoresTouch/choreConstants';

const MealEditModal = ({ open, onClose, date, initialMeal, initialChef, onSave, autoStartRecording = false }) => {
    const [mealText, setMealText] = useState('');
    const [selectedChef, setSelectedChef] = useState('');

    useEffect(() => {
        if (open) {
            setMealText(initialMeal || '');
            setSelectedChef(initialChef || '');
        }
    }, [open, initialMeal, initialChef]);

    const handleSave = () => {
        if (!selectedChef) return;
        onSave(date, mealText, selectedChef);
        onClose();
    };

    const handleTranscription = (text) => {
        setMealText(text);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    width: '100%',
                    maxWidth: 500,
                    p: 3,
                    bgcolor: '#f7fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                }
            }}
        >
            {/* Header: Date + Close */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="900" color="#2d3748">
                    {date && format(new Date(date), 'EEEE, MMM d')}
                </Typography>
                <IconButton onClick={onClose} size="large">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* 1. Meal Result (Text Only) */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'white',
                    border: '2px solid #e2e8f0',
                    minHeight: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {mealText ? (
                    <Typography variant="h5" fontWeight="600" color="#2d3748" align="center" lineHeight={1.4}>
                        {mealText}
                    </Typography>
                ) : (
                    <Typography variant="h5" color="#cbd5e0" align="center" fontWeight="bold">
                        ...
                    </Typography>
                )}
            </Paper>

            {/* 2. Chef Avatars (No Text) */}
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ gap: 2, py: 1 }}>
                {USERS.map((user) => {
                    const isSelected = selectedChef === user.name;
                    return (
                        <Box
                            component="img"
                            key={user.id} 
                            src={`${user.avatar}`}
                            onClick={() => setSelectedChef(user.name)}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/200?text=' + user.name[0] }}
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: isSelected ? `4px solid #2d3748` : '4px solid transparent',
                                opacity: isSelected ? 1 : 0.6,
                                transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                transition: 'all 0.2s',
                                boxShadow: isSelected ? 4 : 1,
                                objectFit: 'cover'
                            }}
                        />
                    );
                })}
            </Stack>

            {/* 3. Actions (Icon Only) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
                {/* Redo Button */}
                <VoiceRecorder 
                    onTranscription={handleTranscription} 
                    validationType="meal"
                    autoStart={autoStartRecording && !mealText}
                >
                    <IconButton
                        sx={{
                            width: 80,
                            height: 80,
                            bgcolor: 'white',
                            color: '#718096',
                            boxShadow: 2,
                            '&:hover': { bgcolor: '#edf2f7' }
                        }}
                    >
                        <ReplayIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                </VoiceRecorder>

                {/* Accept Button */}
                <IconButton
                    onClick={handleSave}
                    disabled={!selectedChef || !mealText}
                    sx={{
                        width: 80,
                        height: 80,
                        bgcolor: !selectedChef || !mealText ? '#cbd5e0' : '#48bb78',
                        color: 'white',
                        boxShadow: 3,
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: !selectedChef || !mealText ? '#cbd5e0' : '#38a169',
                            transform: 'scale(1.05)'
                        }
                    }}
                >
                    <CheckIcon sx={{ fontSize: 50 }} />
                </IconButton>
            </Stack>
        </Dialog>
    );
};

export default MealEditModal;
